/**
 * One-time move of the browser's saved settings from the old `mimic.` key prefix to `boltcrm.` (ADR-1043), so nobody
 * loses a saved view, theme, column choice or dismissed tip when the keys are renamed. It runs once at start (before any
 * module reads a key), is safe to run again (nothing starts with the old prefix any more), and never overwrites a value
 * already saved under the new key.
 */
export const LEGACY_STORAGE_PREFIX = 'mimic.'; // rename:keep
export const STORAGE_PREFIX = 'boltcrm.';

/** The part of `Storage` the migration uses, so tests can pass a plain object. */
export interface StorageLike {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface StorageMigrationResult {
  /** Old keys whose value now lives under the new key. */
  moved: number;
  /** Old keys dropped because the new key already had a value (the new one wins). */
  dropped: number;
}

/** The new key for an old one, or `null` if the key is not an old key. */
export function renamedStorageKey(key: string): string | null {
  return key.startsWith(LEGACY_STORAGE_PREFIX) ? STORAGE_PREFIX + key.slice(LEGACY_STORAGE_PREFIX.length) : null;
}

export function migrateLegacyStorage(storage: StorageLike): StorageMigrationResult {
  const result: StorageMigrationResult = { moved: 0, dropped: 0 };
  // Collect first: changing the storage while walking it skips keys.
  const oldKeys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key !== null && renamedStorageKey(key) !== null) oldKeys.push(key);
  }
  for (const key of oldKeys) {
    const next = renamedStorageKey(key)!;
    try {
      const value = storage.getItem(key);
      if (value !== null && storage.getItem(next) === null) {
        storage.setItem(next, value);
        result.moved++;
      } else {
        result.dropped++;
      }
      storage.removeItem(key);
    } catch {
      // Full or blocked storage: leave the old key where it is; the app works without it.
    }
  }
  return result;
}

/** Runs the migration on this browser's local and session storage; never throws (storage can be blocked). */
export function migrateBrowserStorage(): void {
  const scope = globalThis as unknown as { localStorage?: StorageLike; sessionStorage?: StorageLike };
  for (const name of ['localStorage', 'sessionStorage'] as const) {
    try {
      const storage = scope[name];
      if (storage) migrateLegacyStorage(storage);
    } catch {
      // Storage is blocked (private window, site data off): nothing to move.
    }
  }
}
